import { useAuth } from '../providers/auth-provider';
import { useProductionLogs } from '@t90/api-client';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from '@t90/ui';
import {
  Calendar,
  Clock,
  Package,
  CheckCircle,
} from 'lucide-react';
import { formatDate, formatNumber } from '@t90/common';

export function DashboardPage() {
  const { trainee } = useAuth();
  const productionFilters = { traineeId: trainee?.id, limit: 5 } as any;
  const { data: recentLogs } = useProductionLogs(productionFilters);

  const greeting = getGreeting();
  const totalUnits = recentLogs?.reduce((sum, log) => sum + (log.units_completed ?? 0), 0) ?? 0;
  const totalHours = recentLogs?.reduce((sum, log) => sum + (log.hours_worked ?? 0), 0) ?? 0;

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          {greeting}, {trainee?.full_name?.split(' ')[0] ?? 'Trainee'}!
        </h1>
        <p className="text-muted-foreground">
          Here's your program overview for today.
        </p>
      </div>

      {/* Status Card */}
      <Card className="card-gradient">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Program Status</CardTitle>
              <CardDescription>Your current enrollment status</CardDescription>
            </div>
            <Badge variant={getStatusVariant(trainee?.status ?? 'Pending')}>
              {trainee?.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p className="font-medium">
                  {trainee?.start_date ? formatDate(trainee.start_date) : 'Not set'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="font-medium">{trainee?.location ?? 'Not assigned'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-primary/10 p-2">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cohort</p>
                <p className="font-medium">{trainee?.cohort ?? 'Not assigned'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Units This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalUnits)}</div>
            <p className="text-xs text-muted-foreground">
              from {recentLogs?.length ?? 0} production logs
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Hours This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHours.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">
              {totalHours > 0 ? `${(totalUnits / totalHours).toFixed(1)} units/hr avg` : 'No data'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Your latest production logs</CardDescription>
        </CardHeader>
        <CardContent>
          {recentLogs?.length ? (
            <div className="space-y-4">
              {recentLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-green-100 p-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{formatDate(log.log_date)}</p>
                      <p className="text-sm text-muted-foreground">
                        {log.project_id ? `Project ${log.project_id.substring(0, 8)}...` : 'Unknown Project'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatNumber(log.units_completed ?? 0)} units</p>
                    <p className="text-xs text-muted-foreground">
                      {log.hours_worked?.toFixed(1)} hrs
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No production logs recorded yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Graduated':
      return 'default';
    case 'On Leave':
      return 'warning';
    case 'Terminated':
      return 'destructive';
    case 'Pending':
      return 'secondary';
    default:
      return 'secondary';
  }
}
